import {
    timeout
} from 'widgets/toolbox/util';

const TAU_UNIQUE = 'data-tau-unique';

/**
 * @description Base CheckoutStepMixin implementation
 *
 * This class is not intended to have a separate DOM representation.
 *
 * @param CheckoutMgr Base widget for extending
 */
function CheckoutStepMixinClassCreator(CheckoutMgr: import('widgets/checkout/CheckoutMgr').TCheckoutMgr) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutStepMixin
     * @augments CheckoutMgr
     * @classdesc Represents generic checkout step UI logic. This components handle
     * 1. Activate/Deactivate step sections (Header, Body, Summary)
     * 2. Set focus on step activation/deactivation
     * Main step activation / deactivation logic handled inside CheckoutMgr
     */
    class CheckoutStepMixin extends CheckoutMgr {
        /**
         * @description Handle activate step logic
         */
        activateStep() {
            super.activateStep();
            const currentStepName = this.currentStep;

            this.ref(currentStepName + 'Step')
                .addClass('m-active')
                .attr(TAU_UNIQUE, 'checkout_' + currentStepName);
            this.ref(currentStepName + 'Section').show();
            this.ref(currentStepName + 'Summary').hide();
        }

        /**
         * @description Handle deactivate step logic
         * @param stepName Step name that will be deactivate
         * @param isStepFinished - Flag to detect is step already finished
         */
        deactivateStep(stepName: string, isStepFinished: boolean) {
            super.deactivateStep(stepName, isStepFinished);

            this.ref(stepName + 'Step')
                .removeClass('m-active')
                .attr(TAU_UNIQUE, false);
            this.ref(stepName + 'Section').hide();

            if (isStepFinished) {
                this.ref(stepName + 'Summary').show();
            } else {
                this.ref(stepName + 'Summary').hide();
            }
        }

        /**
         * @description Set focus on the step activation
         * @param currentStep Current checkout step
         */
        setFocusOnFirstInput(currentStep: string) {
            if (this.isStepActivatedFromHistory) {
                return;
            }

            // class AccessibilityFocusMixin, method focusFirstInput
            timeout(() => this.callIfExists('focusFirstInput', currentStep + 'Section', [], true), 200);
        }

        /**
         * @description Set focus on the step deactivation
         * @param currentStep Current checkout step
         */
        setFocusOnEditButton(currentStep: string) {
            if (this.isStepActivatedFromHistory) {
                return;
            }

            // class AccessibilityFocusMixin, method focusFirst
            timeout(() => this.callIfExists('focusFirst', currentStep + 'Step', [], true), 200);
        }
    }

    return CheckoutStepMixin;
}

export type TCheckoutStepMixin = ReturnType<typeof CheckoutStepMixinClassCreator>;

export type TCheckoutStepMixinInstance = InstanceType<TCheckoutStepMixin>;

export default CheckoutStepMixinClassCreator;
